import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import { ArrowDrawIcon } from "@images/svgIcons/arrowDrawIcon"
import {
  DurationType,
  type CouponInputFormik,
  type CouponInputFormikKeys,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useEffect, useMemo, useState } from "react"

type DiscountOfferFormProps = {
  formik: SimpleFormik<CouponInputFormik, CouponInputFormikKeys>
  price: PriceObjectEntity
  isPending: boolean
  isDisableMonthsInput?: boolean
} & React.ComponentProps<"div">

export function DiscountOfferForm({
  formik,
  price,
  isPending,
  isDisableMonthsInput,
  ...props
}: DiscountOfferFormProps) {
  const language = useLanguage()
  const [isDisableMonths, setIsDisableMonths] = useState(false)

  const discountTypesOptions = useMemo(() => {
    return [
      { value: CouponType.PERCENT, label: word("box.member.table.offer.discount.percentInputLabel") },
      { value: CouponType.DIRECT, label: word("box.member.table.offer.discount.directInputLabel") },
    ]
  }, [language])

  const durationTypesOptions = useMemo(() => {
    return [
      { value: DurationType.MONTHLY, label: word("box.member.table.offer.discount.monthlyInputLabel") },
      { value: DurationType.UNLIMITED, label: word("box.member.table.offer.discount.unlimitedInputLabel") },
    ]
  }, [language])

  const priceDiscount = useMemo(() => {
    if (formik.values.value > 0 && price.value > 0) {
      let priceAfterDiscount = price.value
      if (formik.values.type === CouponType.PERCENT) {
        const discountPercentage = formik.values.value / 100
        priceAfterDiscount = priceAfterDiscount - priceAfterDiscount * discountPercentage
      } else if (formik.values.type === CouponType.DIRECT) {
        priceAfterDiscount = priceAfterDiscount - formik.values.value
      }
      return priceAfterDiscount
    } else return null
  }, [formik.values.value, formik.values.type])

  useEffect(() => {
    if (formik.values.durationType === DurationType.UNLIMITED) {
      setIsDisableMonths(true)
      formik.setFieldValue("months", null)
    } else {
      formik.setFieldValue("months", 1)
      setIsDisableMonths(false)
    }
  }, [formik.values.durationType])

  return (
    <div className={cn("flex w-full grow flex-col gap-4", props.className)} {...props}>
      <div className="flex flex-col gap-2">
        <DropdownInput
          label={word("box.member.table.offer.discount.supensionTypeLabel")}
          options={discountTypesOptions}
          isRequiredInput={true}
          error={getFormErrorMessage("type", formik)}
        >
          <Select
            defaultValue={formik.values.type}
            onValueChange={(value) => {
              formik.setFieldValue("type", value)
            }}
            items={discountTypesOptions}
          >
            <SelectTrigger id={"type"}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {discountTypesOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DropdownInput>

        <StandardInput
          getFieldProps={formik.getFieldProps}
          name="value"
          error={getFormErrorMessage("value", formik)}
          setFieldValue={formik.setFieldValue}
          containerClassName="flex flex-row items-center"
          inputProps={{
            type: "number",
            inputMode: formik.values.type === CouponType.PERCENT ? "numeric" : "decimal",
            step: formik.values.type === CouponType.PERCENT ? 1 : 0.1,
            max: formik.values.type === CouponType.PERCENT ? 100 : price.value,
            min: 0,
            disabled: isPending,
            onChange: (e) => {
              if (formik.values.type === CouponType.PERCENT && parseInt(e.target.value) > 100) {
                formik.setFieldValue("value", 100)
              } else if (formik.values.type === CouponType.PERCENT && parseInt(e.target.value) < 0) {
                formik.setFieldValue("value", 1)
              } else if (formik.values.type === CouponType.DIRECT && parseFloat(e.target.value) > price.value) {
                formik.setFieldValue("value", price.value)
              } else {
                formik.setFieldValue(
                  "value",
                  formik.values.type === CouponType.PERCENT ? parseInt(e.target.value) : parseFloat(e.target.value),
                )
              }
            },
          }}
        >
          {formik.values.type === CouponType.PERCENT && (
            <Text fontStyle="TS" className="ml-2 flex items-center justify-center">
              %
            </Text>
          )}
        </StandardInput>
      </div>

      <div className="flex w-full justify-between px-8">
        <div className="flex flex-col items-center">
          <Text fontStyle="LS" className="mt-2 text-textDefault-body">
            {word("box.member.table.offer.discount.offerPriceLabel")}
          </Text>
          <Text
            fontStyle="TM"
            className={cn(
              "mt-2",
              priceDiscount !== null && priceDiscount >= 0 ? "text-textError" : "text-textDefault-body",
            )}
            style={
              priceDiscount !== null && priceDiscount >= 0
                ? { textDecorationThickness: "1px", textDecorationLine: "line-through" }
                : {}
            }
          >
            {formatCurrency(price.value, price.currency, language)}
          </Text>
        </div>
        {priceDiscount !== null && priceDiscount >= 0 ? (
          <ArrowDrawIcon color="currentColor" className="text-textDefault-body" />
        ) : null}
        <div className="flex flex-col items-center">
          <Text fontStyle="LS" className="mt-2 text-textDefault-body">
            {word("box.member.table.offer.discount.offerPriceAfterDiscountLabel")}
          </Text>
          <Text
            fontStyle="TM"
            className={cn(
              "mt-2",
              priceDiscount !== null && priceDiscount >= 0 ? "text-textSuccess" : "text-textDefault-body",
            )}
          >
            {priceDiscount !== null && priceDiscount >= 0
              ? formatCurrency(priceDiscount, price.currency, language)
              : "--"}
          </Text>
        </div>
      </div>
      {isDisableMonthsInput ? null : (
        <div className="flex flex-col gap-2">
          <DropdownInput
            label={word("box.member.table.offer.discount.durationTypeLabel")}
            options={durationTypesOptions}
            isRequiredInput={true}
            error={getFormErrorMessage("durationType", formik)}
          >
            <Select
              defaultValue={formik.values.durationType}
              onValueChange={(value) => {
                formik.setFieldValue("durationType", value)
              }}
              items={durationTypesOptions}
            >
              <SelectTrigger id={"durationType"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationTypesOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DropdownInput>

          {isDisableMonths ? null : (
            <StandardInput
              getFieldProps={formik.getFieldProps}
              name="months"
              error={getFormErrorMessage("months", formik)}
              setFieldValue={formik.setFieldValue}
              containerClassName="flex flex-row items-center"
              inputProps={{
                type: "number",
                inputMode: "numeric",
                step: 1,
                max: 100,
                min: 1,
                disabled: isDisableMonths || isPending,
                onChange: (e) => {
                  formik.setFieldValue("months", parseInt(e.target.value))
                },
              }}
            >
              {formik.values.durationType === DurationType.MONTHLY && (
                <Text fontStyle="TS" className="ml-2 flex items-center justify-center">
                  {word("global.month.label", { count: formik.values.months ?? 1 })}
                </Text>
              )}
            </StandardInput>
          )}
        </div>
      )}
    </div>
  )
}
